// src/App.js
import { useEffect, useRef, useState } from 'react';
import { Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { SnackbarProvider, closeSnackbar } from 'notistack';
import { AppObject, AppWord, MenuUrl } from './value/AppConst';
import { useLazyQuery, useReactiveVar } from '@apollo/client';
import { GET_SESSION_DATA } from './com/login/AppLoginGql';
import UserSessionInfo from './value/UserSessionInfo';
import { alertQueueRepo, appModeRepo, authTableTreeRepo, codeNameRepo, codeTreeRepo, currentMenuRepo, dcInfoListRepo, mobileMenuAuthListRepo, userInfoRepo } from './value/AppModel';
import AppLogin from './com/login/AppLogin';
import AppMain from './com/app_main/AppMain';
import { LIST_MOBILE_MENU_AUTH, makeMobileMenuList } from './com/mobile_menu_auth/MobileMenuAuthGql';
import { useAlertSetter } from './api/app-hook';
import VzmAlert from './common/VzmAlert';
import { CODE_TREE_AND_AUTH_TABLE } from './com/common_code/CommonCodeGql';
import { makeAuthTableToTree } from './com/auth_table/AuthTableGql';
import { useNavigate } from 'react-router-dom';
import TsrMain from './com/app_main/TsrMain';

const PLEASE_READ_AGAIN = '브라우저 다시 읽기를 해 주세요.';

const NoAlert = {open:false, alertInfo:null, onClose: null};

function App() {
    const sessionInfo = useReactiveVar(userInfoRepo);
    const [checkError, setCheckError] = useState(null);
    const appMode = useReactiveVar(appModeRepo);
    const alertQueue = useReactiveVar(alertQueueRepo);
    const [alertNow, setAlertNow] = useState({...NoAlert});
    const addAlert = useAlertSetter();
    const navigate = useNavigate();
    const snackbarRef = useRef(null);
    const startRef = useRef(0);
    const gotCodesRef = useRef(0);


    // ##### GraphQL #####
    const [checkSession, responseCheck] = useLazyQuery(GET_SESSION_DATA,
        {
            ...AppObject.NoCachedFetch,
            onCompleted: (data, option) => {onCompleteCheckSession(data, option)},
            onError: (error) => {setCheckError(error)},
        }
    );

    const [getMobileMenuAuthList, responseList] = useLazyQuery(LIST_MOBILE_MENU_AUTH, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetList(data, option)},
		onError: (error) => addAlert({title: "메뉴 조회 오류", message: [error.message]})
    });

    // CODE_TREE_AND_AUTH_TABLE: code, authTable, dcInfo
    const [getCodeTreeAndAuthTable, responseCodeAndAuth] = useLazyQuery(CODE_TREE_AND_AUTH_TABLE,
        {
            ...AppObject.NoCachedFetch,
            onCompleted: (data, option) => {
                if(data.commonCodeTree && data.authTableList && data.dcInfoList) {

                    const codeNames = {};
                    data.commonCodeTree.forEach((group) => {
                        const map = {__groupName: group.codeGpNm,};
                        group.codes.forEach((code) => {
                            map[code.codeId] = code.codeName;
                        });
                        codeNames[group.codeGp] = map;
                    });

                    codeTreeRepo(data.commonCodeTree)
                    codeNameRepo(codeNames);

                    const secNow = new Date().getTime()/1000;

                    const tree = makeAuthTableToTree(data.authTableList);
                    authTableTreeRepo({when:secNow, tree});

                    dcInfoListRepo({when:secNow, records:data.dcInfoList});
                }
                else if(!data.authTableList) {
                    alert({title:'권한정보 조회 오류', message:[PLEASE_READ_AGAIN]});
                }
                else if(!data.commonCodeTree || !data.dcInfoList) {
                    alert('필수정보 조회 오류:'+PLEASE_READ_AGAIN);
                }
            },
            onError: (error) => {
                alert("필수정보 조회 오류\n" + error.message + "\n" + PLEASE_READ_AGAIN);
            },
        }
    );

    useEffect(()=>{
        if(alertQueue.length > 0) {
            const alertInfo = alertQueue[0];
            const onClose = () => {
                const newQueue = alertQueue.slice(1);
                setAlertNow({...NoAlert});
                alertQueueRepo(newQueue);
            };
            setAlertNow({open:true, alertInfo, onClose});
        }
    }, [alertQueue]);

    useEffect(() => {
        if(startRef.current === 0) {
            document.title = 'Homeplus NewTOP Mobile';
            checkSession();
            startRef.current = 1;
        }
    }, []);

    useEffect(() => {
        if(sessionInfo.signedIn) {
            if(gotCodesRef.current === 0) {
                currentMenuRepo({});
                getMobileMenuAuthList({variables: {}}); // authClassId: will be filled with session info by server.
                getCodeTreeAndAuthTable();
                gotCodesRef.current = 1;
            }
        }
        else {
            navigate("/");
            gotCodesRef.current = 0; // Reset the codes retrieval flag
        }
    }, [sessionInfo.signedIn]);

    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetList = (data, clientOption) => {
        if(data.mobileMenuAuthList) {
            mobileMenuAuthListRepo({when:new Date().getTime()/1000, records:makeMobileMenuList(data.mobileMenuAuthList)});
        }
    };

    const onCompleteCheckSession = (data, option) => {
        if(data.getSessionData) {
            const sessionInfo = UserSessionInfo.initFromUserObject(data.getSessionData);
            userInfoRepo(sessionInfo);
        }
    };

    const onCompleteSuccessLogin = (sessionInfo) => {
        userInfoRepo(sessionInfo);
        if(sessionInfo.user.hasMasterPower()) navigate(MenuUrl.TopHome);
        else if(sessionInfo.user.isTransCompanyUser()) navigate(MenuUrl.TransPlanStateForCompany);
        else navigate(MenuUrl.TransPlanStateForDc);
    };

    const onLogOut = () => {
        gotCodesRef.current = 0;
        mobileMenuAuthListRepo({when:0, records:[]});
    };

    const renderMainOrLogin = () => {
        if(sessionInfo.signedIn) {
            // if(AppModeNow===MainAppMode.Tsr) return <TsrMain />;
            return(<AppMain onLogOut={onLogOut}/>);
        }
        return <AppLogin onLoginSuccess={onCompleteSuccessLogin}/>;
    };

    if(sessionInfo.sessionChecked) {
        if(appMode.isTsrPrinter) {
            return <TsrMain urlPassed={appMode.url} />;
        }

        return (
            <SnackbarProvider
                ref={snackbarRef}
                disableWindowBlurListener={true}
                autoHideDuration={3000}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                action={(snackbarId) => (
                    <IconButton
                        size={AppWord.SMALL}
                        onClick={() => closeSnackbar(snackbarId)}
                    >
                        <CloseIcon fontSize={AppWord.SMALL}/>
                    </IconButton>
                )}
            >
                {renderMainOrLogin()}
                <VzmAlert
                    open={alertNow.open}
                    alertInfo={alertNow.alertInfo}
                    onClose={alertNow.onClose}
                />
            </SnackbarProvider>
        );
    }

    return(
        <Box style={{
                fontSize: '3rem', color: '#5ef',
                display:'flex', justifyContent:'center', alignItems:'center', height:'100vh', flexDirection:'column'
            }}
        >
            잠시만 기다려 주세요...
        </Box>
    );
}

export default App;

